import { throwError as observableThrowError, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MedicalProcedureCaseParameterBase } from '../model/reactive-form-model/medical_procedure_case_parameter_base.model';
import { DropdownItem } from '../model/reactive-form-model/dropdown_type.model';

import { ContinuousAssessmentModel } from '../model/continuous_assessment.model';
import { ContinuousAssessmentFormEntryModel } from '../model/continuous_assessment_form_entries.model';
import { FacultyContinuousAssessmentScoreChartModel } from '../model/faculty_continuous_assessment_score_chart.model';

import { FacultyContinuousAssessmentFormModel } from '../model/faculty_continuous_assessment_form.model';
import { TraineeContinuousAssessmentDetailModel } from '../model/trainee_continuous_assessment_detail.model';
import { TraineeContinuousAssessmentDetailCreateModel } from '../model/trainee_continuous_assessment_detail_create.model';
import { TraineeContinuousAssessmentModel } from '../model/trainee_continuous_assessment.model';
import { ContinuousAssessmentGroupModel } from '../model/continuous_assessment_group.model';
import { TrainerTraineeItemPendingReturnModel } from '../model/trainer_trainee_pending_item.model';
import { TrainerApproveAllModel } from '../model/trainer_approve_all.model';
import { ApiResultModel } from '../model/api_result.model';

import { API_URLS } from '../utils/api_url.util';
import { API_UTILS } from '../utils/api_utils.util';
import { catchError } from 'rxjs/operators';

/*
  Generated class for the UserFaculties provider.

  See https://angular.io/docs/ts/latest/guide/dependency-injection.html
  for more info on providers and Angular 2 DI.
*/
@Injectable()
export class ContinuousAssessmentService {
	public mFacultyContinuousAssessmentForm: FacultyContinuousAssessmentFormModel;

	constructor(private http: HttpClient) {}

	getTraineeFacultyContinuousAssessmentTypes(
		facultyId: string,
		programCategoryId: number
	): Observable<Array<FacultyContinuousAssessmentFormModel>> {
		let items$ = this.http
			.get<Array<FacultyContinuousAssessmentFormModel>>(
				API_URLS.API_GET_TRAINEE_FACULTY_CONTINUOUS_ASSESSMENT_FORM_TYPES +
					`/${facultyId}` +
					`/${programCategoryId}`
			)
			.pipe(catchError(handleError));
		return items$;
	}

	getTrainerFacultyContinuousAssessmentTypes(
		facultyId: string,
		programCategoryId: number
	): Observable<FacultyContinuousAssessmentFormModel> {
		let items$ = this.http
			.get<FacultyContinuousAssessmentFormModel>(
				API_URLS.API_GET_TRAINER_FACULTY_CONTINUOUS_ASSESSMENT_FORM_TYPES +
					`/${facultyId}` +
					`/${programCategoryId}`
			)
			.pipe(catchError(handleError));
		return items$;
	}

	getContinuousAssessmentGroupCriteria(
		facultyContinuousAssessmentGroupId
	): Observable<Array<ContinuousAssessmentModel>> {
		let items$ = this.http
			.get<Array<ContinuousAssessmentModel>>(
				API_URLS.API_GET_CONTINUOUS_ASSESSMENT + `/${facultyContinuousAssessmentGroupId}`
			)
			.pipe(catchError(handleError));
		return items$;
	}

	approveTraineeContinuousAssessment(
		item: TraineeContinuousAssessmentModel
	): Observable<TraineeContinuousAssessmentModel> {
		let body = JSON.stringify(item);
		let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
		let options = { headers: headers };

		let items$ = this.http
			.post<TraineeContinuousAssessmentModel>(API_URLS.API_POST_APPROVE_CONTINUOUS_ASSESSMENT, body, options)
			.pipe(catchError(handleError));
		return items$;
	}

	getTraineeContinuousAssessmentReports(): Observable<Array<TraineeContinuousAssessmentModel>> {
		let items$ = this.http
			.get<Array<TraineeContinuousAssessmentModel>>(API_URLS.API_GET_STUDENT_CONTINUOUS_ASSESSMENT_REPORT)
			.pipe(catchError(handleError));
		return items$;
	}

	getContinuousAssessmentGroup(
		facultyContinuousAssessmentId: string
	): Observable<Array<ContinuousAssessmentGroupModel>> {
		let items$ = this.http
			.get<Array<ContinuousAssessmentGroupModel>>(
				API_URLS.API_GET_CONTINUOUS_ASSESSMENT_GROUP + `/${facultyContinuousAssessmentId}`
			)
			.pipe(catchError(handleError));
		return items$;
	}

	addContinuousAssessment(item: ContinuousAssessmentModel): Observable<ContinuousAssessmentModel> {
		let body = JSON.stringify(item);
		let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
		let options = { headers: headers };

		let items$ = this.http
			.post<ContinuousAssessmentModel>(API_URLS.API_POST_ADD_CONTINUOUS_ASSESSMENT_CRITERIA, body, options)
			.pipe(catchError(handleError));
		return items$;
	}

	editContinuousAssessment(item: ContinuousAssessmentModel): Observable<ContinuousAssessmentModel> {
		let body = JSON.stringify(item);
		let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
		let options = { headers: headers };

		let items$ = this.http
			.post<ContinuousAssessmentModel>(API_URLS.API_POST_EDIT_CONTINUOUS_ASSESSMENT_CRITERIA, body, options)
			.pipe(catchError(handleError));
		return items$;
	}

	addContinuousAssessmentGroup(item: ContinuousAssessmentGroupModel): Observable<ContinuousAssessmentGroupModel> {
		let body = JSON.stringify(item);
		let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
		let options = { headers: headers };

		let items$ = this.http
			.post<ContinuousAssessmentGroupModel>(API_URLS.API_POST_ADD_CONTINUOUS_ASSESSMENT_GROUP, body, options)
			.pipe(catchError(handleError));
		return items$;
	}

	editContinuousAssessmentGroup(item: ContinuousAssessmentGroupModel): Observable<ContinuousAssessmentGroupModel> {
		let body = JSON.stringify(item);
		let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
		let options = { headers: headers };

		let items$ = this.http
			.post<ContinuousAssessmentGroupModel>(API_URLS.API_POST_EDIT_CONTINUOUS_ASSESSMENT_GROUP, body, options)
			.pipe(catchError(handleError));
		return items$;
	}

	getFacultyContinuousAssessmentScoreChart(
		facultyContinuousAssessmentId
	): Observable<Array<FacultyContinuousAssessmentScoreChartModel>> {
		let items$ = this.http
			.get<Array<FacultyContinuousAssessmentScoreChartModel>>(
				API_URLS.API_GET_FACULTY_CONTINUOUS_ASSESSMENT_FORM_SCORE_CHART + `/${facultyContinuousAssessmentId}`
			)
			.pipe(catchError(handleError));
		return items$;
	}

	getFacultyContinuousAssessmentScoreChartByRef(
		refNo
	): Observable<Array<FacultyContinuousAssessmentScoreChartModel>> {
		let items$ = this.http
			.get<Array<FacultyContinuousAssessmentScoreChartModel>>(
				API_URLS.API_GET_FACULTY_CONTINUOUS_ASSESSMENT_FORM_SCORE_CHART_BY_REF + `/${refNo}`
			)
			.pipe(catchError(handleError));
		return items$;
	}

	// get the form which includes the group and entries in a group
	getFacultyContinuousAssessmentForm(
		facultyContinuousAssessmentId
	): Observable<Array<ContinuousAssessmentFormEntryModel>> {
		let items$ = this.http
			.get<Array<ContinuousAssessmentFormEntryModel>>(
				API_URLS.API_GET_FACULTY_CONTINUOUS_ASSESSMENT_FORM + `/${facultyContinuousAssessmentId}`
			)
			.pipe(catchError(handleError));
		return items$;
	}

	// get the form which includes the group and entries in a group
	getTraineeAssessmentFormDetail(traineeAssessmentFormId): Observable<Array<ContinuousAssessmentFormEntryModel>> {
		let items$ = this.http
			.get<Array<ContinuousAssessmentFormEntryModel>>(
				API_URLS.API_GET_TRAINEE_CONTINUOUS_ASSESSMENT_FORM_DETAIL + `/${traineeAssessmentFormId}`
			)
			.pipe(catchError(handleError));
		return items$;
	}

	getTraineeContinuousAssessments(
		traineeId: string,
		type: number
	): Observable<Array<TraineeContinuousAssessmentModel>> {
		let items$ = this.http
			.get<Array<TraineeContinuousAssessmentModel>>(
				API_URLS.API_GET_STUDENT_CONTINUOUS_ASSESSMENTS + `/${traineeId}` + `/${type}`
			)
			.pipe(catchError(handleError));
		return items$;
	}

	getTraineeContinuousAssessmentByRef(refNo: string): Observable<TraineeContinuousAssessmentModel> {
		let items$ = this.http
			.get<TraineeContinuousAssessmentModel>(API_URLS.API_GET_STUDENT_CONTINUOUS_ASSESSMENT_BY_REF + `/${refNo}`)
			.pipe(catchError(handleError));
		return items$;
	}

	getTraineeContinuousAssessmentDetailsByRef(
		refNo: string
	): Observable<Array<TraineeContinuousAssessmentDetailModel>> {
		let items$ = this.http
			.get<Array<TraineeContinuousAssessmentDetailModel>>(
				API_URLS.API_GET_STUDENT_CONTINUOUS_ASSESSMENT_DETAILS_BY_REF + `/${refNo}`
			)
			.pipe(catchError(handleError));
		return items$;
	}

	getTrainerAssessmentByTrainees(userId: string): Observable<Array<TraineeContinuousAssessmentModel>> {
		let items$ = this.http
			.get<Array<TraineeContinuousAssessmentModel>>(
				API_URLS.API_GET_SUPERVISOR_ASSESSMENTS_BY_STUDENT + `/${userId}`
			)
			.pipe(catchError(handleError));
		return items$;
	}

	getTrainersTraineeContinuousAssessments(
		trainerId: string,
		traineeId: string
	): Observable<Array<TraineeContinuousAssessmentModel>> {
		let items$ = this.http
			.get<Array<TraineeContinuousAssessmentModel>>(
				API_URLS.API_GET_SUPERVISOR_STUDENT_CONTINUOUS_ASSESSMENTS + `/${trainerId}` + `/${traineeId}`
			)
			.pipe(catchError(handleError));
		return items$;
	}

	getTrainerTraineesWithPendingAssessments(
		trainerId: string
	): Observable<Array<TrainerTraineeItemPendingReturnModel>> {
		let items$ = this.http
			.get<Array<TrainerTraineeItemPendingReturnModel>>(
				API_URLS.API_GET_TRAINEER_TRAINEES_WITH_PENDING_ASSESSMENTS + `/${trainerId}`
			)
			.pipe(catchError(handleError));
		return items$;
	}

	addTraineeContinuousAssessmentDetail(
		item: TraineeContinuousAssessmentDetailCreateModel
	): Observable<ApiResultModel> {
		let body = JSON.stringify(item);
		let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
		let options = { headers: headers };

		let items$ = this.http
			.post<ApiResultModel>(API_URLS.API_POST_ADD_CONTINUOUS_ASSESSMENT_FORM_DETAIL, body, options)
			.pipe(catchError(handleError));
		return items$;
	}

	initiateContinuousAssessment(item: TraineeContinuousAssessmentModel): Observable<TraineeContinuousAssessmentModel> {
		let body = JSON.stringify(item);
		let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
		let options = { headers: headers };

		let items$ = this.http
			.post<TraineeContinuousAssessmentModel>(API_URLS.API_POST_ADD_CONTINUOUS_ASSESSMENT, body, options)
			.pipe(catchError(handleError));
		return items$;
	}

	mAssessmentEntriesFormGroup(items: MedicalProcedureCaseParameterBase<any>[]) {
		let itemsForm: any = {};

		items.forEach((item) => {
			itemsForm[item.key] = new FormControl(item.value || '', Validators.required);
		});

		return new FormGroup(itemsForm);
	}
}

// this could also be a private method of the component class
function handleError(error: HttpErrorResponse) {
	// log error
	// could be something more sofisticated
	console.error(error);

	return observableThrowError(error.error);
}
